/**
 * CompanyListPage — refonte 9.P.2.R1 UX/layout revamp.
 *
 * Pattern UX commun établi (réutilisable R2 Brokers/Guarantees + R3 Settings) :
 *   - Titre dans le flux ; filtres fixes sur desktop
 *   - Filters bar (search + status tabs)
 *   - Table moderne ≥ md (rounded-xl, header sticky, hover row) + cards stack < md
 *   - Loading skeletons (préserve layout, pas juste « Chargement… »)
 *   - Empty state actionable (icon + titre + sous-titre + CTA brand-600)
 *   - Error state retry (bandeau rouge inline + bouton réessayer)
 *   - Pagination contexte (« Page N — X éléments »)
 *
 * Logique métier strictement préservée : `useCompanies` query, `usePagination`,
 * search debounced, status filter, ApiException + getApiErrorMessage bilingue.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Pagination, usePagination } from "../../components/Pagination";
import { SearchInput } from "../../components/SearchInput";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useCompanies } from "../../lib/queries/companies";

import { CompanyStatusBadge } from "./_components/CompanyStatusBadge";

type StatusFilter = "active" | "archived";

export function CompanyListPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusFilter>("active");
  const pagination = usePagination();

  const { data, isLoading, isFetching, error, refetch } = useCompanies({
    cursor: pagination.cursor,
    q: q || undefined,
    status,
  });

  const onSearchChange = (next: string) => {
    setQ(next);
    pagination.reset();
  };
  const onStatusChange = (next: StatusFilter) => {
    setStatus(next);
    pagination.reset();
  };

  const itemsCount = data?.items.length ?? 0;
  const hasItems = itemsCount > 0;
  const isInitialLoading = isLoading && !data;
  const hasFilter = q.trim() !== "" || status !== "active";

  return (
    <div className="mx-auto w-full max-w-screen-2xl">
      {/* Le titre défile ; seuls les filtres restent accessibles sur desktop. */}
      <header className="-mx-4 border-b border-slate-200 bg-white px-4 py-4 lg:-mx-8 lg:px-8 lg:py-5">
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
          <div className="w-full min-w-0 sm:flex-1">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              {t("companies.title")}
            </h1>
            <p className="mt-1 text-sm text-slate-600">{t("companies.subtitle")}</p>
          </div>
          <Link
            to="/companies/new"
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex shrink-0 touch-manipulation items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M8 3v10M3 8h10"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
            {t("companies.actions.create")}
          </Link>
        </div>
      </header>

      {/* Filters bar */}
      <div className="workspace-command-bar mt-6 flex flex-wrap items-center gap-3 py-2">
        <div data-guide-target="client" className="min-w-0 flex-1 basis-64">
          <SearchInput
            value={q}
            onChange={onSearchChange}
            placeholder={t("companies.search_placeholder")}
          />
        </div>
        <div
          role="tablist"
          aria-label={t("companies.filters.status")}
          className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm shadow-sm"
        >
          {(["active", "archived"] as StatusFilter[]).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={status === key}
              onClick={() => onStatusChange(key)}
              className={
                status === key
                  ? "bg-brand-600 focus-visible:ring-brand-500 rounded-md px-3 py-1.5 text-sm font-medium text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  : "focus-visible:ring-brand-500 rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              }
            >
              {t(`companies.filters.${key}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {error && !isInitialLoading && (
        <div
          role="alert"
          className="mt-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <span aria-hidden="true" className="mt-0.5">
            ⚠
          </span>
          <div className="flex-1">
            <p className="font-medium">
              {error instanceof ApiException
                ? getApiErrorMessage(error, lang) || t("errors.generic")
                : t("errors.generic")}
            </p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-2 touch-manipulation rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              {t("common.retry")}
            </button>
          </div>
        </div>
      )}

      {/* Loading initial — skeletons préserve layout */}
      {isInitialLoading ? (
        <ListSkeleton />
      ) : !hasItems ? (
        hasFilter ? (
          <NoMatchState
            onReset={() => {
              setQ("");
              setStatus("active");
              pagination.reset();
            }}
          />
        ) : (
          <EmptyState />
        )
      ) : (
        <>
          {/* Table desktop ≥ md */}
          <div className="mt-6 hidden overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm md:block">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t("companies.columns.name")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("companies.columns.industry")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("companies.columns.location")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("companies.columns.current_plan")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("companies.columns.status")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.items.map((c) => (
                  <tr
                    key={c.id}
                    className="transition-colors hover:bg-slate-50 motion-reduce:transition-none"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/companies/${c.id}`}
                        className="hover:text-brand-700 focus-visible:ring-brand-500 rounded-sm font-medium text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      >
                        {c.legal_name}
                      </Link>
                      {c.trade_name && c.trade_name !== c.legal_name && (
                        <span className="ml-2 text-xs text-slate-500">{c.trade_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {c.industry ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {c.address_city ? (
                        <>
                          {c.address_city}
                          {c.address_province && (
                            <span className="text-slate-400"> · {c.address_province}</span>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.has_current_plan ? (
                        <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          {t("companies.has_current_plan_yes")}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <CompanyStatusBadge status={c.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards stack mobile < md */}
          <ul className="mt-6 space-y-3 md:hidden">
            {data?.items.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/companies/${c.id}`}
                  className="focus-visible:ring-brand-500 block touch-manipulation rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate text-base font-semibold text-slate-900">
                        {c.legal_name}
                      </h2>
                      {c.trade_name && c.trade_name !== c.legal_name && (
                        <p className="truncate text-xs text-slate-500">{c.trade_name}</p>
                      )}
                    </div>
                    <CompanyStatusBadge status={c.status} />
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {c.industry && (
                      <>
                        <dt className="text-slate-500">{t("companies.columns.industry")}</dt>
                        <dd className="text-slate-700">{c.industry}</dd>
                      </>
                    )}
                    {c.address_city && (
                      <>
                        <dt className="text-slate-500">{t("companies.columns.location")}</dt>
                        <dd className="text-slate-700">
                          {c.address_city}
                          {c.address_province && ` · ${c.address_province}`}
                        </dd>
                      </>
                    )}
                    {c.has_current_plan && (
                      <>
                        <dt className="text-slate-500">{t("companies.columns.current_plan")}</dt>
                        <dd className="text-emerald-700">{t("companies.has_current_plan_yes")}</dd>
                      </>
                    )}
                  </dl>
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs tabular-nums text-slate-500">
              {t("companies.list.page_indicator", {
                page: pagination.pageNumber,
                count: itemsCount,
              })}
            </p>
            <Pagination
              pageNumber={pagination.pageNumber}
              hasPrev={pagination.hasPrev}
              hasNext={!!data?.next_cursor}
              onPrev={pagination.prev}
              onNext={() => pagination.next(data?.next_cursor ?? null)}
              disabled={isFetching}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// States distincts (loading skeleton / empty / no-match) — pattern UX commun
// ---------------------------------------------------------------------------

function ListSkeleton() {
  return (
    <div className="mt-6 hidden overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm md:block">
      <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
        <div className="h-3 w-24 rounded bg-slate-200 motion-safe:animate-pulse motion-reduce:animate-none" />
      </div>
      <div className="divide-y divide-slate-100">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3">
            <div className="h-4 w-40 rounded bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
            <div className="h-3 w-24 rounded bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
            <div className="h-3 w-32 rounded bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
            <div className="ml-auto h-5 w-16 rounded-full bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
      <div
        aria-hidden="true"
        className="bg-brand-50 text-brand-600 grid h-12 w-12 place-items-center rounded-full"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M3 21h18M5 21V7l7-4 7 4v14M9 9v12M15 9v12M9 13h6M9 17h6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h2 className="mt-4 text-base font-semibold text-slate-900">
        {t("companies.empty_state.title")}
      </h2>
      <p className="mt-1 max-w-md text-sm text-slate-600">{t("companies.empty_state.subtitle")}</p>
      <Link
        to="/companies/new"
        className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 mt-4 inline-flex touch-manipulation items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        {t("companies.actions.create")}
      </Link>
    </div>
  );
}

function NoMatchState({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-amber-200 bg-amber-50/40 px-6 py-12 text-center">
      <h3 className="text-base font-semibold text-slate-900">{t("companies.no_match.title")}</h3>
      <p className="mt-1 max-w-md text-sm text-slate-600">{t("companies.no_match.subtitle")}</p>
      <button
        type="button"
        onClick={onReset}
        className="focus-visible:ring-brand-500 mt-4 inline-flex touch-manipulation items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        {t("companies.no_match.reset")}
      </button>
    </div>
  );
}
